import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { routingDocumentTypesApi } from '../routingApi';
import type { RoutingDocumentTypeListItem } from '../routingTypes';
import { sampleRoutingDocumentType } from '../utils/sampleRouting';

/** Create or edit one routing document type. */
export function RoutingDocumentTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: RoutingDocumentTypeListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSave = name.trim().length > 0 && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = { name: name.trim(), description: description.trim() || null };

    try {
      if (editing) await routingDocumentTypesApi.update(editing.id, request);
      else await routingDocumentTypesApi.create(request);

      toast.success(editing ? `Saved ${request.name}.` : `Added ${request.name}.`);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the document type.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'routing-document-type' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit document type' : 'New document type'}
      size="lg"
      id="routing-document-type-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleRoutingDocumentType();
          setName(sample.name);
          setDescription(sample.description ?? '');
        },
        disabled: saving,
      }}
      confirm={{
        id: 'routing-document-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="routing-document-type-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="routing-document-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <TextField
          id="routing-document-type-name"
          label="Name"
          required
          description="What the kind of document is called, e.g. Letter or Memorandum. Held once among the live rows, and offered on both the Receiving and the Dispatching form."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={150}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <TextField
          id="routing-document-type-description"
          label="Description"
          description="Optional note for whoever maintains the catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
          error={error?.fieldError('description')}
        />
      </div>
    </Modal>
  );
}
